import { z } from "zod";
import { DeclineMailSent, VendorQuoteCompareItem } from "./orderBundle.js";
import { VendorQuoteRfqCandidate, VendorQuoteRow } from "./vendorQuoteList.js";

// ---------------------------------------------------------------------------
// 外注見積の取込と突合（#81、docs/33、DB 047。1回目）
//   外注先の見積（PDF・写真）を取り込む → GPU 側の処理役（apps/gpu-worker）が読み取る → 自社見積の行に当てる（候補）
//   → 突合の画面で人が確かめる → 「確定」で仕入れの確度が quoted になる。
//   画面の言葉：「登録する」「取込」「当てる」「確定」。読み取りは「AI読み取り」（2026-10-11 夜の射場さんの決定で「AI」を名乗る。R3＝受け取った書類と同じ言葉）
// ---------------------------------------------------------------------------

const Yen = z.number().int().min(-999_999_999_999).max(999_999_999_999);
const Qty = z.number().min(0).max(99_999_999).nullable();

/** 取り込みの状態（vendor_quotes.ocr_status ＋ confirmed_at） */
export const VendorQuoteStatus = z.enum(["queued", "reading", "review", "done", "failed", "manual"]);
export type VendorQuoteStatus = z.infer<typeof VendorQuoteStatus>;
/** 2026-09-27 射場さん（#89）：待ち＝「待機中」（処理役がまだ取りに来ていない。取込が多いとき）、読み取り中＝くるくる回るしるし＋「AI読み取り中」（R3 で「読取中」から）、済み＝緑の ✓「読取済」 */
/** #274（docs/99 §4）：札は名詞（「手で打っています」「読み取れませんでした」「確定（旧）」から）。見積書の外注見積の帯の状態の列は一覧と同じ言葉（vendorQuoteStateLabel）に替えた */
export const VENDOR_QUOTE_STATUS_LABEL: Record<VendorQuoteStatus, string> = {
  queued: "待機中",
  reading: "AI読み取り中",
  review: "読取済",
  done: "確定",
  failed: "読取失敗",
  manual: "手入力",
};

/** 読み取った行の種類。detail だけ突合の対象、ほかは検算・表示に使う */
export const VendorLineRole = z.enum(["detail", "subtotal", "rate", "discount", "tax", "total", "heading", "excluded", "adjustment", "zero"]);
export type VendorLineRole = z.infer<typeof VendorLineRole>;

/** 元（画面の「元」の列）。PDF は読み取りのあとテキスト層の有無で言い換える */
export const VendorQuoteSource = z.enum(["upload", "mobile_camera", "fax"]);
export type VendorQuoteSource = z.infer<typeof VendorQuoteSource>;

/** 取り込める中身と大きさ（決めごと4） */
export const VENDOR_QUOTE_MAX_BYTES = 20 * 1024 * 1024;
export const VENDOR_QUOTE_MAX_PAGES = 10;
export const VENDOR_QUOTE_CONTENT_TYPES = ["application/pdf", "image/jpeg", "image/png"] as const;
/** 登録の「ファイル選択」の accept（PC・スマホとも。FileDrop に渡す。TIFF は受けない＝処理役が読めない） */
export const VENDOR_QUOTE_ACCEPT = VENDOR_QUOTE_CONTENT_TYPES.join(",");

/** 月の通数の上限は billing.ts の READS_PER_MONTH（受け取った書類の読み取りと合算。../eigyouryoku/docs/13 決めごと12）。受付開始前はだれも制限しない＝#69 と同じ扉 */

/** 読み取った行（vendor_quote_lines） */
export const VendorQuoteLine = z.object({
  id: z.string().uuid(),
  lineNo: z.number().int(),
  role: VendorLineRole,
  name: z.string(),
  spec: z.string(),
  quantity: Qty,
  unit: z.string(),
  unitPrice: Yen.nullable(),
  /** 金額の無い行（在庫・込・別途）は null */
  amount: Yen.nullable(),
  note: z.string(),
  /** 備考の列の文字（051、#87。定価＝8,800 など。取込で自社の行の備考に写る＝紙に出る） */
  remarks: z.string().default(""),
  /** 読み取った元の文字（人が直したあとも残る） */
  rawText: z.string().nullable(),
  pageNo: z.number().int().nullable(),
  /** 読み取った紙の名前（表紙・内訳書…）。無ければ null（049：取込の画面の紙の帯） */
  sheetName: z.string().nullable().default(null),
  /** 当てた自社見積の行（行の系譜）。null＝当てていない（「見積に無い行」） */
  mappedLineageId: z.string().uuid().nullable(),
  mappedBy: z.enum(["ai", "human", "rule"]).nullable(),
  mappedConfidence: z.number().nullable(),
  corrected: z.boolean(),
  /** 「手で打った値のまま」（048 決めごと c）。当てた系譜のどれかが true なら、確定でもその系譜の仕入れは書かない */
  keepOwnCost: z.boolean().default(false),
});
export type VendorQuoteLine = z.infer<typeof VendorQuoteLine>;

/** 自社見積の行（突合の左側。売価は社内の画面なので見せる） */
export const VendorQuoteOwnLine = z.object({
  lineageId: z.string().uuid(),
  lineNo: z.number().int(),
  name: z.string(),
  spec: z.string(),
  quantity: Qty,
  unit: z.string(),
  unitPrice: Yen.nullable(),
  amount: Yen,
  /** いまの仕入れ（none なら null）と出どころ */
  costAmount: Yen.nullable(),
  costSource: z.enum(["none", "manual", "vendor_quote", "standard_unit_price", "ai_estimated", "own"]),
  /** 仕入れの確度（ルール7）。confirmed＝手で打った値（048：確定で上書きする前に画面で見せる） */
  costConfidence: z.enum(["none", "assumed", "quoted", "confirmed"]).default("none"),
  costVendorQuoteId: z.string().uuid().nullable(),
});
export type VendorQuoteOwnLine = z.infer<typeof VendorQuoteOwnLine>;

/** 配分の結果（fn_vendor_quote_allocation） */
export const VendorQuoteAllocation = z.object({
  /** 当てた行（系譜）ごと。lineageId null＝見積に無い行の分 */
  rows: z.array(z.object({ lineageId: z.string().uuid().nullable(), baseCost: Yen, allocatedCost: Yen })),
  /** 見積の行に入る仕入れの合計 */
  total: Yen,
  /** 見積に無い行の分（入らない） */
  unmapped: Yen,
  detailSum: Yen,
  /** 明細に当たらない行の和（当たった行も含む。表示用） */
  adjustSum: Yen,
  /** 配分の元手＝当たっていない「明細に当たらない行」の和（048 決めごと b。OFF のときは「〜円 は仕入れに入っていません」） */
  poolSum: Yen.default(0),
});
export type VendorQuoteAllocation = z.infer<typeof VendorQuoteAllocation>;

export const VendorQuoteSummary = z.object({
  id: z.string().uuid(),
  status: VendorQuoteStatus,
  vendor: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
  /** 読み取った会社名（外注先が未選択のときの候補に） */
  issuerName: z.string().nullable(),
  quoteNo: z.string(),
  quotedOn: z.string().nullable(),
  receivedAt: z.string(),
  /** 元：PDF（テキスト層あり）・PDF（画像）・写真（スマホ）。#89 でテキスト層ありは「PDF」とだけ */
  sourceLabel: z.string(),
  pageCount: z.number().int().nullable(),
  totalExTax: Yen.nullable(),
  /** 税込の合計（#89：案件の「外注」タブの一覧は税込を大きく、税抜を下に小さく） */
  totalIncTax: Yen.nullable(),
  taxIncluded: z.boolean(),
  document: z.object({ id: z.string().uuid(), documentNo: z.string().nullable(), subject: z.string() }).nullable(),
  group: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
  errorMessage: z.string().nullable(),
  confirmedAt: z.string().nullable(),
});
export type VendorQuoteSummary = z.infer<typeof VendorQuoteSummary>;

export const VendorQuoteList = z.object({
  items: z.array(VendorQuoteSummary),
  /** 当月の通数と上限（上限が null＝受付開始前で制限なし） */
  quota: z.object({ used: z.number().int(), limit: z.number().int().nullable() }),
});
export type VendorQuoteList = z.infer<typeof VendorQuoteList>;

/** 検算（決めごと7）。ok／mismatch／unknown（手で打つ・未読み取り） */
export const VendorQuoteCheck = z.object({
  result: z.enum(["ok", "mismatch", "unknown"]),
  note: z.string().nullable(),
});

export const VendorQuote = VendorQuoteSummary.extend({
  taxRate: z.number(),
  taxAmount: Yen.nullable(),
  readMethod: z.enum(["text_layer", "vision", "text_layer_vision", "manual"]).nullable(),
  check: VendorQuoteCheck,
  allocateAdjustments: z.boolean(),
  lines: z.array(VendorQuoteLine),
  ownLines: z.array(VendorQuoteOwnLine),
  allocation: VendorQuoteAllocation,
  /** 原本の中身（画面の <object>／<img> の src に。content type も） */
  original: z.object({ contentType: z.string(), url: z.string() }),
  /** 読み取りにかかった秒（ai_jobs.gpu_ms）。無ければ null */
  readSeconds: z.number().nullable(),
  /** 049：この外注見積の行 → どの見積のどの行に入ったか（見積の紙の「取込」で入れたもの。編集している回で数える） */
  importedLines: z.array(z.object({ vendorLineId: z.string().uuid(), documentId: z.string().uuid(), displayNo: z.string().nullable(), subject: z.string(), lineNo: z.number().int(), name: z.string() })).default([]),
  /** #198：一覧の1行と同じ形（状態・理由・担当者・不採用・見積依頼・版）。右側パネルの頭はこれ */
  row: VendorQuoteRow.nullable().default(null),
  /** #198：見積依頼とつながっていないとき、{vendor}が同じ回答待ちの見積依頼（同じ案件が先）。2通以上なら画面で選ぶ */
  rfqCandidates: z.array(VendorQuoteRfqCandidate).default([]),
  /** #233 2a：回答の比較（同じ{project}・同じ件名の見積依頼への回答。2件以上のときだけ。自分も入る） */
  compare: z.array(VendorQuoteCompareItem).default([]),
  /** #233 2a：辞退の連絡を送った記録（いちばん新しいもの） */
  declineSent: DeclineMailSent.nullable().default(null),
});
export type VendorQuote = z.infer<typeof VendorQuote>;

/** POST /vendor-quotes の query（本文は原本そのもの。Content-Type で判定） */
export const VendorQuoteCreateQuery = z.object({
  /** #198：案件は任意（届いた FAX を先に登録し、案件は後から。docs/79 決定3） */
  groupId: z.string().uuid().optional(),
  /** #198：見積依頼の画面の「回答登録」から。{vendor}・案件を依頼から入れ、登録の瞬間に回答あり */
  rfqId: z.string().uuid().optional(),
  documentId: z.string().uuid().optional(),
  vendorId: z.string().uuid().optional(),
  source: VendorQuoteSource.default("upload"),
  /** 元のファイル名（画面の表示だけ。保存の名前には使わない） */
  name: z.string().max(200).optional(),
});
export type VendorQuoteCreateQuery = z.infer<typeof VendorQuoteCreateQuery>;

/** PUT /vendor-quotes/:id（頭）。省いた欄は変えない */
export const VendorQuoteUpdate = z.object({
  vendorId: z.string().uuid().nullable().optional(),
  documentId: z.string().uuid().nullable().optional(),
  quoteNo: z.string().max(50).optional(),
  quotedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  taxIncluded: z.boolean().optional(),
  allocateAdjustments: z.boolean().optional(),
  /** 手で打つときの税抜合計（読み取りが無い） */
  totalExTax: Yen.nullable().optional(),
  /** #198：案件（null＝未設定）。反映した行があれば変えられない（409 VENDOR_QUOTE_IMPORTED） */
  groupId: z.string().uuid().nullable().optional(),
  /** #198：担当者（null＝未設定） */
  assigneeId: z.string().uuid().nullable().optional(),
  /** #198：件名（空なら一覧は原本のファイル名） */
  title: z.string().trim().max(200).optional(),
  /** #198：版（VendorQuoteRow.updatedAt）。送ったときだけ見る＝違えば 409 VENDOR_QUOTE_STALE */
  version: z.string().max(40).optional(),
});
export type VendorQuoteUpdate = z.infer<typeof VendorQuoteUpdate>;

/** PUT /vendor-quotes/:id/lines：読み取った行を直す・手で打つ行を足す・消す（確定前だけ） */
export const VendorQuoteLineInput = z.object({
  /** 無ければ新しい行（手で打つ） */
  id: z.string().uuid().optional(),
  role: VendorLineRole.default("detail"),
  name: z.string().max(200),
  spec: z.string().max(200).default(""),
  quantity: Qty.default(null),
  unit: z.string().max(20).default(""),
  unitPrice: Yen.nullable().default(null),
  amount: Yen.nullable().default(null),
  note: z.string().max(50).default(""),
  remarks: z.string().max(200).default(""),
});
export type VendorQuoteLineInput = z.infer<typeof VendorQuoteLineInput>;
export const VendorQuoteLinesUpdate = z.object({
  lines: z.array(VendorQuoteLineInput).max(500),
});
export type VendorQuoteLinesUpdate = z.infer<typeof VendorQuoteLinesUpdate>;

/** PUT /vendor-quotes/:id/mappings：対応の付け替え（人が当てた）。lineageId を省けば対応は変えず、keepOwnCost（手で打った値のまま）だけ変える（048） */
export const VendorQuoteMappingsUpdate = z.object({
  mappings: z.array(z.object({ lineId: z.string().uuid(), lineageId: z.string().uuid().nullable().optional(), keepOwnCost: z.boolean().optional() })).max(500),
});
export type VendorQuoteMappingsUpdate = z.infer<typeof VendorQuoteMappingsUpdate>;

/** ホームの「本日の業務」に足す数 */
export const VendorQuoteTodo = z.object({
  review: z.number().int(),
  failed: z.number().int(),
  reading: z.number().int(),
});
export type VendorQuoteTodo = z.infer<typeof VendorQuoteTodo>;

export const VENDOR_QUOTE_MSG = {
  notFound: "外注見積が見つかりません",
  type: "PDF・JPEG・PNG のファイルを選んでください",
  tooBig: "ファイルは 20MB までです",
  tooManyPages: "PDF は 10ページまでです",
  broken: "ファイルを読めませんでした。別のファイルを選んでください",
  quota: "今月の取り込みの上限に達しました（{used}／{limit}通）。来月になると、また取り込めます",
  groupNotFound: "{project}が見つかりません",
  documentNotFound: "当てる見積が見つかりません",
  documentMismatch: "当てる見積は同じ{project}の見積から選んでください",
  vendorNotFound: "{vendor}が見つかりません",
  queued: "AI読み取りを始めました。終わったら「本日の業務」に出ます（2ページで 2〜3分。ページが多いほど長く）",
  notReady: "AI読み取りが終わってから確定できます",
  confirmed: "すでに確定しています",
  notConfirmed: "確定していません",
  confirmedLines: "仕入れを {n}行に入れました（{vendor} {no}）",
  unconfirmed: "確定を戻しました。仕入れは未入力に戻りました",
  manual: "手で打つ形にしました。行を打って、自社見積の行に当ててください",
  deleted: "取り込みをやめました",
  cannotDeleteConfirmed: "確定した外注見積は消せません。先に「確定解除」を押してください",
  cannotDeleteImported: "取り込んだ行がある外注見積は消せません。見積の行の「解除」で戻してからです",
  registered: "登録しました。AI読み取りが終わると、見積書の明細の「取込」で行を選べます（2ページで 2〜3分。ページが多いほど長く）",
  lockedAfterConfirm: "確定したあとは修正できません。先に「確定解除」を押してください",
  noDocument: "当てる見積を選んでください",
  confirmNote: "AI読み取り結果は確認のうえ確定してください。確定すると見積の仕入れに入ります（見積に無い行は入りません）。",
  /** 048：手で打った仕入れが変わる行の数（確定ボタンの下） */
  manualChanges: "手で打った仕入れが変わる行 {n}行（行の「手で打った値のまま」で残せます）",
  /** 048 決めごと b：配分の帯 */
  poolNotIncluded: "当たっていない行 {yen}円 は仕入れに入っていません",
  poolIncluded: "当たっていない行 {yen}円 を当てた行に金額の割合で配分します",
  /** #82：当てる見積に行が無い */
  noOwnLines: "当てる見積に行がありません。見積に行を書くか、ほかの見積に当ててください",
  importNoLines: "この見積にはまだ行がありません。読み取った行を当てる相手が無いので、先に見積に行を書くか、別の見積を選んでください（取り込みだけ先にしてもかまいません）",
  documentChanged: "当てる見積を変えました",
  failedBand: "3回試しましたが読み取れませんでした。",
  /** 9/29（#92）：読み取れなかったときの説明（帯の「？」） */
  failedHelp: "写真が暗い・小さいときに起きます。手で打つか、撮り直して登録し直してください。",
  slow: "時間がかかっています。手で打つこともできます。",
  /** 9/29（#92）：「仕入れは税抜で入れます」は外注見積の中身の見出しの「？」へ */
  taxNote: "税込を税抜に換算しました（{rate}%、四捨五入）。",
  costsSaved: "仕入れを保存しました",
  /** #168：検算・取込は帯をやめ、結果は通知・状態は見出しの右のチップ（docs/10 §5.12） */
  checkOk: "検算：合計が合っています（{note}）",
  checkMismatch: "検算：合計が合いません。{note}。読み取りの誤りかもしれません",
  checkChipOk: "検算 一致",
  checkChipMismatch: "検算 要確認 {n}",
  taxChip: "税抜に換算",
  importedChip: "取込 {done}／{all}行",
  importHelp: "取込先の見積書の明細で、社内用の列の「取込」から行を選びます。",
  noEstimate: "この{project}にはまだ見積がありません。見積を作成すると、その明細の「取込」で行を選べます",
} as const;

/** 状態を1つの言葉に（ocr_status ＋ confirmed_at） */
export function vendorQuoteStatusOf(ocrStatus: string, confirmedAt: Date | string | null): VendorQuoteStatus {
  if (confirmedAt) return "done";
  switch (ocrStatus) {
    case "pending":
      return "queued";
    case "processing":
      return "reading";
    case "done":
      return "review";
    case "failed":
      return "failed";
    case "skipped":
      return "manual";
    default:
      return "review";
  }
}

/** 自社見積の1行の状態（突合の画面のしるし。docs/33 付録A）。色でなく文字で伝える。costChange＝手で打った仕入れが確定で変わる（048） */
export type OwnLineMark = { kind: "ok" | "check" | "none" | "manual"; label: string; why: string; costChange?: { from: number; to: number } };
export const OWN_LINE_MARK_LABEL = { ok: "合う", check: "確かめて", none: "回答なし", manual: "手入力あり" } as const;

const fmtQty = (q: number | null): string => (q == null ? "" : (Math.round(q * 100) / 100).toLocaleString("ja-JP"));

const fmtYen = (n: number): string => n.toLocaleString("ja-JP");

/**
 * 自社見積の行に当てた読み取りの行から、しるしと理由を決める（試作 v1 と同じ決まり。決めごと21）
 *   ・人が当てた行は「合う」（数量・単位の警告を出さない）
 *   ・単位が違えば「確かめて」。同じ単位で「式」でなければ数量の和を比べる
 *   ・似た名前で当てた（信頼度 0.9 未満）は「確かめて」
 *   ・048：当てた行があり、自社に手で打った仕入れ（manual）があるとき＝金額が同じなら「合う（手で打った値と同じ）」、
 *     違えば「確かめて（手で打った 8,250 → 22,000）」（costChange）、「手で打った値のまま」なら「手入力あり（そのまま）」。
 *     cost.alloc＝確定で入る仕入れ（配分後。DB の fn_vendor_quote_allocation）、cost.keep＝当てた行のどれかが keepOwnCost
 */
export function ownLineMark(
  own: { quantity: number | null; unit: string; costAmount: number | null; costSource: string; costVendorQuoteId: string | null },
  mapped: VendorQuoteLine[],
  quoteId: string,
  cost: { alloc: number | null; keep: boolean } = { alloc: null, keep: false },
): OwnLineMark {
  const ls = mapped.filter((l) => l.amount != null);
  if (!ls.length) {
    if (own.costAmount != null && (own.costSource === "manual" || (own.costSource === "vendor_quote" && own.costVendorQuoteId !== quoteId))) {
      return { kind: "manual", label: OWN_LINE_MARK_LABEL.manual, why: `仕入れ ${fmtYen(own.costAmount)} は${own.costSource === "manual" ? "手で打った値" : "別の外注見積の値"}のまま` };
    }
    return { kind: "none", label: OWN_LINE_MARK_LABEL.none, why: "この見積には当たる行がありません" };
  }
  const manual = own.costSource === "manual" && own.costAmount != null;
  if (manual && cost.keep) {
    const to = cost.alloc == null ? "" : `（外注の ${fmtYen(cost.alloc)} は入れません）`;
    return { kind: "manual", label: OWN_LINE_MARK_LABEL.manual, why: `手で打った値 ${fmtYen(own.costAmount!)} のまま${to}` };
  }
  const why: string[] = [];
  if (ls.length > 1) why.push(`${ls.length}行を足しました`);
  let kind: "ok" | "check" = "ok";
  if (ls.every((l) => l.mappedBy === "human")) {
    why.push("手で当てました");
  } else {
    // 数量・単位の無い行（明細に当たらない行）を当てたときは比べない（048）
    const withQty = ls.filter((l) => l.unit || l.quantity != null);
    const units = [...new Set(withQty.map((l) => l.unit))];
    if (withQty.length && units.length === 1) {
      const u = units[0]!;
      const qty = withQty.reduce((t, l) => t + (l.quantity ?? 0), 0);
      if (u !== own.unit) why.push(`単位が違います（${own.unit || "（なし）"} ≠ ${u || "（なし）"}）`);
      else if (u !== "式" && own.quantity != null && qty !== own.quantity) why.push(`数量が違います（${fmtQty(own.quantity)} ${own.unit} ≠ ${fmtQty(qty)} ${u}）`);
    }
    const weak = ls.filter((l) => (l.mappedConfidence ?? 1) < 0.9);
    if (weak.length) why.push(`似た名前で当てました（${weak.slice(0, 2).map((l) => l.name).join("・")}${weak.length > 2 ? " ほか" : ""}）`);
    if (why.some((w) => /違います|似た名前/.test(w))) kind = "check";
  }
  let costChange: OwnLineMark["costChange"];
  if (manual && cost.alloc != null) {
    if (cost.alloc === own.costAmount) why.push("手で打った値と同じ");
    else {
      why.push(`手で打った ${fmtYen(own.costAmount!)} → ${fmtYen(cost.alloc)}`);
      costChange = { from: own.costAmount!, to: cost.alloc };
      kind = "check";
    }
  }
  const out: OwnLineMark = { kind, label: OWN_LINE_MARK_LABEL[kind], why: why.join("。") };
  if (costChange) out.costChange = costChange;
  return out;
}
